import { z } from "zod";
export const OpsCalendarScopeSchema = z.enum([
    "ALL_SCHEDULED",
    "ENGAGEMENTS",
    "TASK_DEADLINES",
    "FOLLOW_UPS",
    "SCHEDULED_MESSAGES"
]);
// WHY [BOL-CALENDAR-CANONICAL-PROJECTION-001]: Calendar rendering and Agent
// citation fences must classify the same structured Work fields. Keeping two
// look-alike filters caused the visible Calendar and its answer scope to drift.
// This function owns membership only; Work remains the sole state store and
// each consumer owns its presentation. Falsifier: the Web and Agent can admit
// different Work for the same scope/range, or a title word creates an event.
export function opsCalendarProjection(input) {
    const scheduledByWork = new Map();
    for (const state of input.workAgentStates) {
        if (state.stage !== "SCHEDULED" || !state.scheduledAt || !state.scheduleTimeZone)
            continue;
        const values = scheduledByWork.get(state.workItemId) ?? [];
        values.push(state);
        scheduledByWork.set(state.workItemId, values);
    }
    const entries = [];
    for (const item of input.workItems) {
        if (item.status === "DONE" || item.status === "ARCHIVED")
            continue;
        if (admitsCalendarKind(input.scope, "ENGAGEMENT")
            && (item.workKind ?? "TASK") === "ENGAGEMENT"
            && item.scheduledStartAt) {
            entries.push({
                workItemId: item.id,
                kind: "ENGAGEMENT",
                occursAt: item.scheduledStartAt,
                timeZone: input.workspaceTimeZone,
                allDay: item.scheduledAllDay === true,
                sourceId: null
            });
        }
        if (admitsCalendarKind(input.scope, "DUE") && item.dueAt) {
            entries.push({
                workItemId: item.id,
                kind: "DUE",
                occursAt: item.dueAt,
                timeZone: input.workspaceTimeZone,
                allDay: false,
                sourceId: null
            });
        }
        if (admitsCalendarKind(input.scope, "CHECKPOINT")
            && item.nextCheckpointAt
            && !sameCalendarMinute(item.nextCheckpointAt, item.dueAt)) {
            entries.push({
                workItemId: item.id,
                kind: "CHECKPOINT",
                occursAt: item.nextCheckpointAt,
                timeZone: input.workspaceTimeZone,
                allDay: false,
                sourceId: null
            });
        }
        if (admitsCalendarKind(input.scope, "SCHEDULED_MESSAGE")) {
            for (const state of scheduledByWork.get(item.id) ?? []) {
                entries.push({
                    workItemId: item.id,
                    kind: "SCHEDULED_MESSAGE",
                    occursAt: state.scheduledAt,
                    timeZone: state.scheduleTimeZone,
                    allDay: false,
                    sourceId: state.proposalId ?? null
                });
            }
        }
    }
    return entries;
}
function admitsCalendarKind(scope, kind) {
    if (scope === "ALL_SCHEDULED")
        return true;
    return scope === (kind === "ENGAGEMENT" ? "ENGAGEMENTS"
        : kind === "DUE" ? "TASK_DEADLINES"
            : kind === "CHECKPOINT" ? "FOLLOW_UPS"
                : "SCHEDULED_MESSAGES");
}
function sameCalendarMinute(left, right) {
    return Boolean(right)
        && Math.floor(new Date(left).getTime() / 60_000) === Math.floor(new Date(right).getTime() / 60_000);
}
const DayKeySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).refine((value) => {
    const date = new Date(`${value}T12:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, "Expected a real ISO calendar day.");
export const OpsWorkLensSchema = z.enum(["BOARD", "TIMELINE", "TABLE"]);
export const OpsAssistantWorkspaceContextSchema = z.discriminatedUnion("surface", [
    z.object({ surface: z.literal("OPS_CHAT") }).strict(),
    z.object({
        surface: z.literal("BOARD"),
        workLens: OpsWorkLensSchema.optional(),
        visibleWorkItemIds: z.array(z.uuid()).max(32).optional(),
        visibleWorkItemsTruncated: z.boolean().optional(),
        focusedWorkItemId: z.uuid().nullable().optional()
    }).strict().superRefine((value, context) => {
        if (value.focusedWorkItemId && !(value.visibleWorkItemIds ?? []).includes(value.focusedWorkItemId)) {
            context.addIssue({ code: "custom", path: ["focusedWorkItemId"], message: "Focused Work must be visible in the current Work lens." });
        }
    }),
    z.object({ surface: z.literal("APPROVALS") }).strict(),
    z.object({
        surface: z.literal("CALENDAR"),
        calendarScope: OpsCalendarScopeSchema,
        visibleStartDay: DayKeySchema,
        visibleEndDayExclusive: DayKeySchema,
        selectedDay: DayKeySchema,
        visibleWorkItemIds: z.array(z.uuid()).max(32).optional(),
        visibleWorkItemsTruncated: z.boolean().optional(),
        focusedWorkItemId: z.uuid().nullable().optional()
    }).strict().superRefine((value, context) => {
        if (value.visibleEndDayExclusive <= value.visibleStartDay) {
            context.addIssue({ code: "custom", path: ["visibleEndDayExclusive"], message: "Calendar range must end after it starts." });
        }
        if (value.selectedDay < value.visibleStartDay || value.selectedDay >= value.visibleEndDayExclusive) {
            context.addIssue({ code: "custom", path: ["selectedDay"], message: "Selected day must be inside the visible Calendar range." });
        }
        if (value.focusedWorkItemId
            && value.visibleWorkItemIds
            && !value.visibleWorkItemIds.includes(value.focusedWorkItemId)) {
            context.addIssue({ code: "custom", path: ["focusedWorkItemId"], message: "Focused Work must be visible in Calendar." });
        }
    })
]);
export const OpsAssistantWorkspaceReferenceSchema = z.object({
    surface: z.enum(["CALENDAR", "BOARD", "APPROVALS"]),
    behavior: z.enum(["REFERENCE", "OPEN"]),
    focusDay: DayKeySchema.nullable(),
    workLens: OpsWorkLensSchema.nullable()
}).strict().superRefine((value, context) => {
    if (value.surface !== "CALENDAR" && value.focusDay !== null) {
        context.addIssue({ code: "custom", path: ["focusDay"], message: "Only Calendar references may focus a day." });
    }
    if ((value.surface === "BOARD") !== (value.workLens !== null)) {
        context.addIssue({ code: "custom", path: ["workLens"], message: "Board references require exactly one Work lens." });
    }
});
//# sourceMappingURL=ops-assistant-context.js.map